import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { useQuery } from "@tanstack/react-query";
import { ViewCustomer } from "collins-js-sdk";
import { Search, X } from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { fetchCustomersFromAPI } from "../../../../../APIs/Customer/GetCustomers";
import { useDiaryContext } from "../../../../../hooks/useDiaryContext";
import { CustomerCard, EmptyState, InitialState } from "./FindCustomer";

type FindCustomerModalProps = {
  open: boolean;
  selectedCustomer: (customer: ViewCustomer) => void;
  handleClose: () => void;
  onCreateNew?: () => void;
};

const FindCustomerModal = ({
  open,
  selectedCustomer,
  handleClose,
  onCreateNew
}: FindCustomerModalProps) => {
  const [searchText, setSearchText] = useState<string>("");
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);
  const bookingContext = useDiaryContext();
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  const venueId = bookingContext.venueConfig?._id ?? "";

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["findCustomer", searchText, venueId],
    queryFn: () =>
      fetchCustomersFromAPI({
        query: searchText,
        associatedVenues: [venueId]
      }),
    enabled: searchText.length > 2,
    staleTime: 30000 // Cache for 30 seconds
  });

  const customerList = !searchText ? [] : (data ?? []);
  const hasSearched = searchText.length > 2;
  const showEmpty = hasSearched && !isLoading && customerList.length === 0;
  const showResults = hasSearched && customerList.length > 0;

  // Auto-focus search input when modal opens
  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchText("");
      setFocusedIndex(-1);
    }
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showResults) return;

      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setFocusedIndex((prev) =>
            prev < customerList.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : -1));
          break;
        case "Enter":
          e.preventDefault();
          if (focusedIndex >= 0 && customerList[focusedIndex]) {
            handleSelectCustomer(customerList[focusedIndex]);
          }
          break;
        case "Escape":
          e.preventDefault();
          handleClose();
          break;
      }
    },
    [showResults, focusedIndex, customerList, handleClose]
  );

  // Scroll focused item into view
  useEffect(() => {
    if (focusedIndex >= 0 && listRef.current) {
      const focusedElement = listRef.current.children[
        focusedIndex
      ] as HTMLElement;
      if (focusedElement) {
        focusedElement.scrollIntoView({
          block: "nearest",
          behavior: "smooth"
        });
      }
    }
  }, [focusedIndex]);

  const handleSelectCustomer = (customer: ViewCustomer) => {
    selectedCustomer(customer);
    handleClose();
  };

  const handleClearSearch = () => {
    setSearchText("");
    setFocusedIndex(-1);
    inputRef.current?.focus();
  };

  const handleCreateNewCustomer = () => {
    onCreateNew?.();
    handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent
        className="sm:max-w-[500px] sm:w-[500px] w-[calc(100%-2rem)] p-0 gap-0"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b">
          <DialogTitle className="text-xl">Find Customer</DialogTitle>
          <DialogDescription>
            Search by name, email, or phone number
          </DialogDescription>
        </DialogHeader>

        {/* Search Input */}
        <div className="px-6 pt-4 pb-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              ref={inputRef}
              role="search"
              placeholder="Search customers..."
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                setFocusedIndex(-1);
              }}
              className="pl-9 pr-9 h-11 w-full"
              autoComplete="none"
            />
            <div className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 flex items-center justify-center">
              {searchText && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={handleClearSearch}
                >
                  <X className="h-4 w-4" />
                </Button>
              )}
            </div>
          </div>

          {/* Results count */}
          {showResults && (
            <div className="flex items-center justify-between mt-3">
              <p className="text-sm font-medium text-muted-foreground">
                {customerList.length}{" "}
                {customerList.length === 1 ? "result" : "results"}
              </p>
              {isFetching && (
                <Badge variant="outline" className="text-xs">
                  Updating...
                </Badge>
              )}
            </div>
          )}
        </div>

        {/* Content Area */}
        <div className="px-6 pb-6">
          {/* Loading State */}
          {isLoading && hasSearched && (
            <div className="space-y-3 py-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-3 p-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-[200px]" />
                    <Skeleton className="h-3 w-[150px]" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Results List */}
          {showResults && !isLoading && (
            <div
              ref={listRef}
              className="space-y-2 max-h-[400px] overflow-y-auto pr-2 -mr-2 py-2 w-full"
            >
              {customerList.map((customer, index) => {
                const customerId = customer.id ?? index;
                return (
                  <CustomerCard
                    key={customerId}
                    customer={customer}
                    focused={index === focusedIndex}
                    onClick={() => handleSelectCustomer(customer)}
                    onHover={() => setFocusedIndex(index)}
                  />
                );
              })}
            </div>
          )}

          {/* Empty State */}
          {showEmpty && (
            <EmptyState
              searchText={searchText}
              onCreateNew={onCreateNew ? handleCreateNewCustomer : undefined}
            />
          )}

          {/* Initial State */}
          {!hasSearched && <InitialState />}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default React.memo(FindCustomerModal);
